import {
  head,
  cond,
  compact,
  constant,
  find,
  defaultTo,
  flow,
  iteratee,
  startsWith,
  stubTrue,
  map,
  isNil,
  negate,
  isEqual,
  some,
} from "lodash/fp";

import {
  ApiRecommendation,
  ApiRecommendationAction,
  Recommendation,
  RecommendationAction,
  RecommendationActionMetadata,
  RecommendationMeetingContext,
} from "@app/analytics/types";
import { get, post } from "@app/common/api";
import {
  camelizeKeys,
  parseLocalDateTimeFromString,
  parseUTCDateTime,
} from "@app/utils/utils";

const BACKUP_DEFAULT_ACTION: ApiRecommendation["actions"][number] = {
  kind: "acknowledge",
  default: true,
  label: "Acknowledge",
  operationToken: "<unknown>",
};

const getDefaultAction = (
  apiRec: ApiRecommendation
): ApiRecommendation["actions"][number] => {
  if (apiRec.actions.length === 0) return BACKUP_DEFAULT_ACTION;

  const defaultAction = find({ default: true }, apiRec.actions);
  const firstAction = head(apiRec.actions)!;
  return defaultAction ?? { ...firstAction, default: true };
};

const hasActionItemKind = flow([
  iteratee("kind"),
  isEqual("ActionItemParticipantRecommendation"),
]);

const hasActionItemLabel = flow([
  getDefaultAction,
  defaultTo({ label: "Review" }),
  iteratee("label"),
  startsWith("Review your action item"),
]);

const isActionItemRecommendation = cond<ApiRecommendation, boolean>([
  [hasActionItemKind, constant(true)],
  [hasActionItemLabel, constant(true)],
  [stubTrue, constant(false)],
]);

const hasCoachingRecommendationLabel = flow([
  getDefaultAction,
  iteratee("label"),
  startsWith("Review your coaching recommendation"),
]);

const isCoachingRecommendation = cond<ApiRecommendation, boolean>([
  [hasCoachingRecommendationLabel, constant(true)],
]);

const parseLabel = cond<ApiRecommendation, string>([
  [isCoachingRecommendation, constant("Coaching")],
  [isActionItemRecommendation, constant("Action item for you")],
  [
    flow([getDefaultAction, negate(isNil)]),
    flow([getDefaultAction, iteratee("label")]),
  ],
  [stubTrue, constant("Review")],
]);

const parseActionMetadata: (
  action: ApiRecommendationAction
) => RecommendationActionMetadata | null = (action) => {
  if (action.actionMetadata == null || action.actionMetadata === "null") {
    return null;
  }

  const json = camelizeKeys(
    typeof action.actionMetadata === "string"
      ? JSON.parse(action.actionMetadata)
      : action.actionMetadata
  );

  return {
    ...json,
    startTime: json.startTime ? parseUTCDateTime(json.startTime) : null,
    endTime: json.endTime ? parseUTCDateTime(json.endTime) : null,
  };
};

const parseActions: (
  actions: ApiRecommendationAction[]
) => RecommendationAction[] = (actions) => {
  const parsedActions = map(
    (action: ApiRecommendationAction) => ({
      ...action,
      label: startsWith("Review your action item", action.label)
        ? "Action item for you"
        : action.label,
      actionMetadata: parseActionMetadata(action),
    }),
    actions
  );

  // if there is no default action, set the first one to be the default
  if (parsedActions.length > 0 && !some({ default: true }, parsedActions)) {
    parsedActions[0].default = true;
  }

  return parsedActions;
};

/** Placeholder meetingContext for coaching recommendations, grouped together in the same fake session. */
const COACHING_MEETING_CONTEXT: RecommendationMeetingContext = {
  userId: "COACHING_USER_ID",
  role: "attendee",
  title: "Coaching",
  sessionIds: ["COACHING_SESSION_123"], // Downstream groupRecommendations groups by sessionId
  meetingSchedule: {
    startTime: "",
    endTime: "",
  },
};

const parseRecommendations: (
  recommendations: ApiRecommendation[]
) => Recommendation[] =
  // flat list where if there are sub recommendations add those or if not then add the current recommendation
  map((rec: ApiRecommendation) => ({
    ...rec,
    meetingContext: isCoachingRecommendation(rec)
      ? COACHING_MEETING_CONTEXT
      : rec.meetingContext,
    actions: rec.actions?.length
      ? parseActions(rec.actions)
      : parseActions([BACKUP_DEFAULT_ACTION]),
    label: parseLabel(rec),
    kind: isActionItemRecommendation(rec) ? "action_item" : rec.kind,
    completedOn: rec.completedOn
      ? parseLocalDateTimeFromString(rec.completedOn)
      : undefined,
  }));

export const getRecommendationsForUser = async (options?: {
  authBearer?: string;
}) => {
  const response = await get("/users/me/recommendations", {
    withCreds: true,
    authBearer: options?.authBearer,
  });

  return parseRecommendations(response?.data?.recommendations?.meetings ?? []);
};

export const postRecommendationAction = async ({
  action,
  isAccepted,
}: {
  action: RecommendationAction;
  isAccepted: boolean;
}): Promise<any> => {
  return await post(
    "/recommendations/execute",
    {
      isAccepted,
      operationToken: action.operationToken,
    },
    { withCreds: true }
  );
};

export const bulkPostRecommendationsAction = async ({
  recommendations,
  isAccepted,
}: {
  recommendations: {
    action: RecommendationAction;
  }[];
  isAccepted: boolean;
}): Promise<any> => {
  const results = await Promise.allSettled(
    map(async (rec) => {
      await postRecommendationAction({
        action: rec.action,
        isAccepted,
      });
    }, recommendations)
  );

  return compact(
    results.map((result, i) =>
      result.status === "rejected" ? recommendations[i] : null
    )
  );
};
